import React from 'react';

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'overlay' | 'inline';
  message?: string;
  fullScreen?: boolean;
  theme?: 'light' | 'dark';
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'medium', 
  variant = 'default', 
  message,
  fullScreen = false,
  theme = 'dark'
}) => {
  const isDark = theme === 'dark';
  
  const sizeStyles = {
    small: { width: '24px', height: '24px', borderWidth: '2px' },
    medium: { width: '40px', height: '40px', borderWidth: '3px' },
    large: { width: '60px', height: '60px', borderWidth: '4px' }
  };

  const spinnerStyle = {
    ...sizeStyles[size],
    border: `${sizeStyles[size].borderWidth} solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#f3f3f3'}`,
    borderTop: `${sizeStyles[size].borderWidth} solid ${isDark ? '#FFD700' : '#e94d1a'}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    ...(variant === 'overlay' && {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: isDark ? 'rgba(10, 14, 23, 0.98)' : 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(2px)',
      zIndex: 9999
    }),
    ...(variant === 'inline' && {
      padding: '20px'
    }),
    ...(variant === 'default' && {
      padding: fullScreen ? '20vh 20px' : '40px 20px',
      backgroundColor: isDark ? '#0A0E17' : 'transparent',
      minHeight: fullScreen ? '100vh' : 'auto'
    })
  };

  const messageStyle: React.CSSProperties = {
    color: isDark ? '#94A3B8' : '#666',
    fontSize: size === 'small' ? '14px' : '16px',
    fontWeight: '500',
    textAlign: 'center',
    margin: 0,
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif"
  };

  return (
    <div style={containerStyle}>
      <div style={spinnerStyle}></div>
      {message && <p style={messageStyle}>{message}</p>}
    </div>
  );
};

// Pulse loading animation for skeleton loading
export const PulseLoader: React.FC<{ className?: string; theme?: 'light' | 'dark' }> = ({ className, theme = 'dark' }) => {
  const isDark = theme === 'dark';
  
  const pulseStyle: React.CSSProperties = {
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f0f0f0',
    borderRadius: '8px',
    animation: 'pulse 1.5s ease-in-out infinite',
    ...(className === 'pulse-text' && {
      height: '16px',
      marginBottom: '8px'
    }),
    ...(className === 'pulse-title' && {
      height: '24px',
      marginBottom: '12px'
    }),
    ...(className === 'pulse-card' && {
      height: '100px',
      marginBottom: '16px'
    })
  };

  return <div style={pulseStyle} className={className}></div>;
};

// Dots loading animation
export const DotsLoader: React.FC<{ message?: string; theme?: 'light' | 'dark' }> = ({ message, theme = 'dark' }) => {
  const isDark = theme === 'dark';
  
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '40px',
    backgroundColor: isDark ? '#0A0E17' : 'transparent'
  };

  const dotsContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '4px'
  };

  const dotStyle: React.CSSProperties = {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: isDark ? '#FFD700' : '#e94d1a',
    animation: 'bounce 1.4s ease-in-out infinite both'
  };

  return (
    <div style={containerStyle}>
      <div style={dotsContainerStyle}>
        <div style={{ ...dotStyle, animationDelay: '-0.32s' }}></div>
        <div style={{ ...dotStyle, animationDelay: '-0.16s' }}></div>
        <div style={dotStyle}></div>
      </div>
      {message && (
        <p style={{ 
          color: isDark ? '#94A3B8' : '#666', 
          fontSize: '16px', 
          margin: 0, 
          textAlign: 'center',
          fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif"
        }}>
          {message}
        </p>
      )}
    </div>
  );
};

export default LoadingSpinner;